import {
  ActionIcon,
  Alert,
  Box,
  Center,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconArrowUp } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, podatki } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { StanjeNalaganja } from '../postavitev/Podatki';
import { barve } from '../tema';
import { useAiStanje } from '../vozila/branje';
import { porabljeno, shraniOdgovor, usePogovor, useZmoznosti, zamenjajSporocilo, type Sporocilo } from './asistent';
import { KarticaAsistenta, SeznamZmoznosti } from './KarticaAsistenta';

/**
 * Pogovor z asistentom (v predalu in na strani /asistent): sporočila, vnos vprašanja (Enter pošlje, Shift+Enter nova
 * vrstica) in pri praznem pogovoru seznam zmožnosti z vzorčnimi vprašanji (brezplačno). {@code naPogovor} se pokliče,
 * ko prvo vprašanje ustvari nov pogovor.
 */
export function PogovorAsistenta({
  pogovorId,
  naPogovor,
}: {
  pogovorId: number | null;
  naPogovor: (id: number) => void;
}) {
  const odjemalec = useQueryClient();
  const pogovor = usePogovor(pogovorId);
  const stanje = useAiStanje();
  const [besedilo, setBesedilo] = useState('');
  const [cakajoce, setCakajoce] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [napakaPolja, setNapakaPolja] = useState<string | null>(null);
  const konec = useRef<HTMLDivElement>(null);
  const sporocila = pogovorId === null ? [] : (pogovor.data?.sporocila ?? []);

  useEffect(() => {
    konec.current?.scrollIntoView({ block: 'end' });
  }, [sporocila.length, cakajoce]);

  const vprasaj = async (v: string) => {
    const vprasanje = v.trim();
    if (!vprasanje || cakajoce !== null) {
      return;
    }
    setNapaka(null);
    setNapakaPolja(null);
    setCakajoce(vprasanje);
    setBesedilo('');
    try {
      const o = await podatki(
        api.POST('/api/asistent/vprasanje', { body: { pogovorId: pogovorId ?? undefined, besedilo: vprasanje } }),
      );
      shraniOdgovor(odjemalec, o);
      if (pogovorId === null && o.pogovorId) {
        naPogovor(o.pogovorId);
      }
    } catch (e) {
      setBesedilo(vprasanje);
      if (e instanceof NapakaApi && e.polja.besedilo) {
        setNapakaPolja(e.polja.besedilo);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setCakajoce(null);
    }
  };

  const neNaVoljo = stanje.data && !stanje.data.naVoljo ? stanje.data.razlog : null;

  return (
    <Stack gap={0} h="100%" style={{ minHeight: 0 }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
        <Stack gap="sm" p="xs" role="log" aria-label="Pogovor z asistentom">
          {pogovorId !== null && !pogovor.data ? (
            pogovor.isError ? (
              <StanjeNalaganja
                napaka={pogovor.error}
                ponovi={() => void pogovor.refetch()}
                naslov="Pogovora ni bilo mogoče naložiti"
              />
            ) : (
              <Center py="xl">
                <Loader aria-label="Nalagam pogovor" />
              </Center>
            )
          ) : sporocila.length === 0 && cakajoce === null ? (
            <Dobrodoslica vprasaj={(v) => void vprasaj(v)} />
          ) : (
            sporocila.map((s) => (
              <SporociloAsistenta
                key={s.id}
                s={s}
                vprasaj={(v) => void vprasaj(v)}
                naSpremembo={(novo) => pogovorId !== null && zamenjajSporocilo(odjemalec, pogovorId, novo)}
              />
            ))
          )}
          {cakajoce !== null && (
            <>
              <Oblacek desno>{cakajoce}</Oblacek>
              <Group gap="xs" c="dimmed" role="status">
                <Loader size="xs" type="dots" />
                <Text size="sm" c="dimmed">
                  Razmišljam …
                </Text>
              </Group>
            </>
          )}
          <div ref={konec} />
        </Stack>
      </ScrollArea>
      <Stack gap="xs" pt="xs" style={{ borderTop: `1px solid ${barve.obroba}` }}>
        {neNaVoljo && (
          <Alert color="yellow" variant="light" icon={<IconAlertCircle size={18} />} p="xs">
            {neNaVoljo}
          </Alert>
        )}
        {napaka && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} p="xs" role="alert">
            {napaka}
          </Alert>
        )}
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <Textarea
            style={{ flex: 1 }}
            size="md"
            autosize
            minRows={1}
            maxRows={5}
            placeholder="Vprašajte asistenta …"
            aria-label="Vprašanje za asistenta"
            value={besedilo}
            error={napakaPolja}
            maxLength={2000}
            onChange={(e) => {
              setBesedilo(e.currentTarget.value);
              setNapakaPolja(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void vprasaj(besedilo);
              }
            }}
          />
          <Tooltip label="Pošlji (Enter)">
            <ActionIcon
              size={42}
              radius="xl"
              aria-label="Pošlji vprašanje"
              onClick={() => void vprasaj(besedilo)}
              loading={cakajoce !== null}
              disabled={!besedilo.trim()}
            >
              <IconArrowUp size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Stack>
    </Stack>
  );
}

/** Prazen pogovor: kaj asistent zna, z vzorčnimi vprašanji (s strežnika, brez klica modela). */
function Dobrodoslica({ vprasaj }: { vprasaj: (v: string) => void }) {
  const zmoznosti = useZmoznosti();
  return (
    <Stack gap="sm" py="xs">
      <Text fw={700}>Pozdravljeni! Vprašajte me s svojimi besedami.</Text>
      <Text size="sm" c="dimmed">
        Številke, ki vam jih pokažem, so vedno neposredno iz vaših podatkov. Vsako vprašanje se zaračuna v točkah.
      </Text>
      {zmoznosti.data ? (
        <SeznamZmoznosti zmoznosti={zmoznosti.data} vprasaj={vprasaj} />
      ) : zmoznosti.isError ? (
        <StanjeNalaganja
          napaka={zmoznosti.error}
          ponovi={() => void zmoznosti.refetch()}
          naslov="Zmožnosti ni bilo mogoče naložiti"
        />
      ) : (
        <Loader size="sm" aria-label="Nalagam" />
      )}
    </Stack>
  );
}

function SporociloAsistenta({
  s,
  vprasaj,
  naSpremembo,
}: {
  s: Sporocilo;
  vprasaj: (v: string) => void;
  naSpremembo: (s: Sporocilo) => void;
}) {
  if (s.vloga === 'UPORABNIK') {
    return <Oblacek desno>{s.besedilo}</Oblacek>;
  }
  return (
    <Stack gap={4}>
      {s.kartica ? (
        <KarticaAsistenta kartica={s.kartica} vprasaj={vprasaj} sporociloId={s.id} naSpremembo={naSpremembo} />
      ) : (
        <Oblacek>{s.besedilo}</Oblacek>
      )}
      {s.zaracunano !== undefined && s.zaracunano !== null && (
        <Text size="xs" c="dimmed">
          Porabljeno: {porabljeno(s.zaracunano)}
        </Text>
      )}
    </Stack>
  );
}

function Oblacek({ desno = false, children }: { desno?: boolean; children: ReactNode }) {
  return (
    <Box
      maw="88%"
      ml={desno ? 'auto' : 0}
      px="md"
      py="xs"
      style={{
        background: desno ? barve.izbrano : barve.povrsinaPolje,
        border: `1px solid ${barve.obroba}`,
        borderRadius: 12,
      }}
    >
      <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {children}
      </Text>
    </Box>
  );
}
